import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Mail, Plus, Ticket, Trash2, UserPlus, Users } from 'lucide-react';
import type { Invite, WorkspaceRole } from '../../shared/types';
import { WORKSPACE_ROLES, roleAtLeast } from '../../shared/types';
import { PageHeader } from '../components/layout';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyButton,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  Skeleton,
  useToast,
} from '../components/ui';
import { MemberRow } from '../components/team/MemberRow';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { formatDay } from '../lib/format';
import { useSessionStore } from '../stores/session';
import { useWorkspaceRole, useWorkspaceStore } from '../stores/workspace';

const ROLE_OPTIONS = WORKSPACE_ROLES.map((role) => ({
  value: role,
  label: role[0]!.toUpperCase() + role.slice(1),
}));

/**
 * Role options an actor may actually assign. Mirrors the API: only an owner can
 * grant or change the owner role (PATCH /:id/members/:userId 403s otherwise),
 * so an admin never sees an option the server would reject.
 */
function roleOptionsFor(actorRole: WorkspaceRole | null) {
  return actorRole === 'owner'
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((option) => option.value !== 'owner');
}

function inviteUrl(token: string): string {
  return `${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`;
}

function inviteStatus(invite: Invite): 'pending' | 'expired' | 'accepted' {
  if (invite.acceptedAt) return 'accepted';
  if (invite.expiresAt <= Date.now()) return 'expired';
  return 'pending';
}

/* ================================================================== */
/* Invite dialog                                                       */
/* ================================================================== */

function CreateInviteDialog({
  open,
  onOpenChange,
  workspaceId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('member');
  const [result, setResult] = useState<{ inviteUrl: string; emailSent: boolean; email: string } | null>(
    null,
  );

  const reset = () => {
    setEmail('');
    setRole('member');
    setResult(null);
  };

  const create = useMutation({
    mutationFn: () => api.workspaces.createInvite(workspaceId, { email: email.trim(), role }),
    onSuccess: (response) => {
      onCreated();
      if (response.emailSent) {
        toast({ title: `Invite sent to ${response.invite.email}`, tone: 'success' });
        onOpenChange(false);
        reset();
      } else {
        setResult({ inviteUrl: response.inviteUrl, emailSent: false, email: response.invite.email });
      }
    },
    onError: (error) => {
      toast({
        title: 'Could not create the invite',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  /** Single close path so every trigger (X, backdrop, Escape, buttons) resets the form. */
  const close = () => {
    if (create.isPending) return;
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && close()}
      title="Invite a teammate"
      description={result ? undefined : 'They join this workspace only, with the role you choose.'}
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              leftIcon={<UserPlus className="size-4" />}
              loading={create.isPending}
              disabled={!email.trim()}
              onClick={() => create.mutate()}
            >
              Send invite
            </Button>
          </>
        )
      }
    >
      {result ? (
        <Alert tone="info" title="Email delivery is not configured" icon={<Ticket />}>
          <p>Share this link with {result.email} — it accepts the invite when opened.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-surface px-2 py-1 font-mono text-[11px] text-fg">
              {result.inviteUrl}
            </code>
            <CopyButton value={result.inviteUrl} />
          </div>
        </Alert>
      ) : (
        <div className="space-y-4">
          <Field label="Email" htmlFor="invite-email" required>
            <Input
              id="invite-email"
              type="email"
              autoFocus
              leadingIcon={<Mail />}
              placeholder="teammate@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label="Role" htmlFor="invite-role">
            <Select
              id="invite-role"
              value={role}
              onChange={(event) => setRole(event.target.value as WorkspaceRole)}
              options={ROLE_OPTIONS}
            />
          </Field>
        </div>
      )}
    </Dialog>
  );
}

/* ================================================================== */
/* Page                                                                 */
/* ================================================================== */

/**
 * Team — owned by WEB-4. Members table (role changes + removal, gated by
 * permission) and invites (create dialog, pending list with a copyable
 * link, revoke).
 */
export function TeamPage() {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const currentUser = useSessionStore((state) => state.user);
  const refreshSession = useSessionStore((state) => state.refresh);
  const role = useWorkspaceRole();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const canManage = role ? roleAtLeast(role, 'admin') : false;
  const isOwner = role === 'owner';

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<
    { userId: string; label: string; isSelf: boolean } | null
  >(null);
  const [revokeTarget, setRevokeTarget] = useState<Invite | null>(null);

  const membersQuery = useQuery({
    queryKey: qk.members(workspaceId),
    queryFn: () => api.workspaces.members(workspaceId as string),
    enabled: Boolean(workspaceId),
  });

  const invitesQuery = useQuery({
    queryKey: qk.invites(workspaceId),
    queryFn: () => api.workspaces.invites(workspaceId as string),
    enabled: Boolean(workspaceId) && canManage,
  });

  const members = membersQuery.data?.members ?? [];
  const pendingInvites = (invitesQuery.data?.invites ?? []).filter(
    (invite) => inviteStatus(invite) !== 'accepted',
  );
  const ownerCount = members.filter((member) => member.role === 'owner').length;

  const updateRole = useMutation({
    mutationFn: ({ userId, nextRole }: { userId: string; nextRole: WorkspaceRole }) =>
      api.workspaces.updateMember(workspaceId as string, userId, { role: nextRole }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.members(workspaceId) });
      toast({ title: 'Role updated', tone: 'success' });
    },
    onError: (error) => {
      toast({
        title: 'Could not update that role',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.workspaces.removeMember(workspaceId as string, userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.members(workspaceId) });
      toast({ title: 'Member removed' });
    },
    onError: (error) => {
      toast({
        title: 'Could not remove that member',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const revokeInvite = useMutation({
    mutationFn: (inviteId: string) => api.workspaces.deleteInvite(workspaceId as string, inviteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.invites(workspaceId) });
      toast({ title: 'Invite revoked' });
    },
    onError: (error) => {
      toast({
        title: 'Could not revoke that invite',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  if (!workspaceId) return null;

  return (
    <>
      <PageHeader
        title="Team"
        description="Members and invitations for this workspace."
        actions={
          canManage ? (
            <Button leftIcon={<Plus className="size-4" />} onClick={() => setInviteOpen(true)}>
              Invite teammate
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-8">
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-fg">Members</h2>
          {membersQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : membersQuery.isError ? (
            <Alert tone="danger" title="Could not load the member list">
              {membersQuery.error instanceof ApiError
                ? membersQuery.error.message
                : 'Try refreshing the page.'}
            </Alert>
          ) : members.length === 0 ? (
            <EmptyState
              icon={<Users className="size-5" />}
              title="You are the only member"
              description="Invite teammates by email. They get owner, admin or member access to this workspace only."
            />
          ) : (
            <Card className="overflow-hidden">
              {members.map((member) => {
                const isSelf = member.userId === currentUser?.id;
                const isLastOwner = member.role === 'owner' && ownerCount <= 1;
                const label = member.user.name ?? member.user.email;

                // Same three rules the API enforces: touching an owner row (in
                // either direction) needs owner, the last owner can never be
                // demoted, and anyone may remove themselves but not if they are
                // the last owner.
                const mayTouchOwnerRow = member.role !== 'owner' || isOwner;
                const editable = canManage && mayTouchOwnerRow && !isLastOwner;
                const mayRemove = isSelf
                  ? !isLastOwner
                  : canManage && mayTouchOwnerRow && !isLastOwner;

                return (
                  <MemberRow
                    key={member.id}
                    member={member}
                    roleSlot={
                      editable ? (
                        <Select
                          aria-label={`Role for ${label}`}
                          selectSize="sm"
                          wrapperClassName="w-32"
                          value={member.role}
                          disabled={updateRole.isPending && updateRole.variables?.userId === member.userId}
                          onChange={(event) =>
                            updateRole.mutate({
                              userId: member.userId,
                              nextRole: event.target.value as WorkspaceRole,
                            })
                          }
                          options={roleOptionsFor(role)}
                        />
                      ) : undefined
                    }
                    actions={
                      mayRemove ? (
                        <Button
                          variant="ghost"
                          size={isSelf ? 'xs' : 'icon-sm'}
                          aria-label={isSelf ? 'Leave this workspace' : `Remove ${label}`}
                          onClick={() =>
                            setRemoveTarget({ userId: member.userId, label, isSelf })
                          }
                        >
                          {isSelf ? 'Leave' : <Trash2 className="size-3.5" />}
                        </Button>
                      ) : undefined
                    }
                  />
                );
              })}
            </Card>
          )}
        </section>

        {canManage && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-fg">Pending invites</h2>
            {invitesQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : invitesQuery.isError ? (
              <Alert tone="danger" title="Could not load pending invites">
                {invitesQuery.error instanceof ApiError
                  ? invitesQuery.error.message
                  : 'Try refreshing the page.'}
              </Alert>
            ) : pendingInvites.length === 0 ? (
              <p className="text-sm text-muted">No open invitations.</p>
            ) : (
              <Card className="divide-y divide-border overflow-hidden">
                {pendingInvites.map((invite) => {
                  const status = inviteStatus(invite);
                  return (
                    <div key={invite.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-medium text-fg">{invite.email}</p>
                          <Badge tone="neutral" className="capitalize">
                            {invite.role}
                          </Badge>
                          {status === 'expired' && (
                            <Badge tone="warning" dot>
                              Expired
                            </Badge>
                          )}
                        </div>
                        <p className="flex items-center gap-1 text-xs text-subtle">
                          <Clock className="size-3" aria-hidden />
                          {status === 'expired' ? 'Expired' : 'Expires'} {formatDay(invite.expiresAt)}
                        </p>
                      </div>
                      {status === 'pending' && <CopyButton value={inviteUrl(invite.token)} label="Copy link" />}
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Revoke invite to ${invite.email}`}
                        onClick={() => setRevokeTarget(invite)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </Card>
            )}
          </section>
        )}
      </div>

      {canManage && (
        <CreateInviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          workspaceId={workspaceId}
          onCreated={() => void queryClient.invalidateQueries({ queryKey: qk.invites(workspaceId) })}
        />
      )}

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(next) => !next && setRemoveTarget(null)}
        title={
          !removeTarget
            ? 'Remove member?'
            : removeTarget.isSelf
              ? 'Leave this workspace?'
              : `Remove ${removeTarget.label}?`
        }
        description={
          removeTarget?.isSelf
            ? 'You lose access immediately and will need a fresh invite to come back.'
            : 'They lose access to this workspace immediately.'
        }
        confirmLabel={removeTarget?.isSelf ? 'Leave workspace' : 'Remove'}
        destructive
        onConfirm={async () => {
          if (!removeTarget) return;
          const leaving = removeTarget.isSelf;
          await removeMember.mutateAsync(removeTarget.userId);
          setRemoveTarget(null);
          if (leaving) {
            // The active workspace is gone — re-read the session so the shell
            // switches to whatever else this user still belongs to.
            setWorkspace(null);
            await refreshSession();
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        onOpenChange={(next) => !next && setRevokeTarget(null)}
        title={revokeTarget ? `Revoke the invite to ${revokeTarget.email}?` : 'Revoke invite?'}
        confirmLabel="Revoke"
        destructive
        onConfirm={async () => {
          if (!revokeTarget) return;
          await revokeInvite.mutateAsync(revokeTarget.id);
          setRevokeTarget(null);
        }}
      />
    </>
  );
}

export default TeamPage;
